import { AIM_PRESET_LABELS, type AimPreset } from '../combat/autoaim';
import type { WeaponDef } from '../combat/weapon';
import type { Rarity } from '../economy/rarity';
import type { LootKind } from '../loot/droptable';

/** I-1 (PLAN-CITY3 §5): the fly-arc's own per-kind glyph — 'cosmetic'/'collectible' are never batched through `spawnFlyArc` (both already get their own toast/burst moment), included anyway so the map stays exhaustive over `LootKind`. */
const FLY_ICON: Record<LootKind, string> = { coin: '●', material: '⬡', part: '⚙', heart: '❤️', cosmetic: '✨', collectible: '📍' };

/**
 * S02's player-facing combat HUD: HP bar, super meter + button, and an
 * aim-assist cycle button — so Karan can compare the 3 presets live on his
 * phone during G1 testing without `?dev=1` (the dev overlay's own switcher,
 * wired in dev/overlay.ts, is a secondary verification-only path). Plain DOM
 * (not canvas), same choice S00/S01's overlay/jump-button already made.
 * S04 added a weapon-cycle button (same idiom as the aim button) and a coin
 * counter — materials aren't shown here yet (nothing consumes them until
 * S10/S11's crafting), the dev overlay is the place to check that count.
 * S05 added an interact button + prompt pill (world/interact.ts's nearest
 * in-range Interactable, or hidden when none) and a self-timed toast
 * (checkpoint-discovery/locked-seam messages, world/checkpoints.ts +
 * world/interact.ts) — deliberately separate from `banner`, which stays
 * the death-screen's own manually shown/hidden element; a toast that
 * happened to fire during the death window must never race it.
 * S06 added a top-center boss HP bar (`showBossBar`/`hideBossBar`/
 * `updateBossBar`, hidden by default) — placed top-center specifically so
 * it can never collide with `.br3d-hud`'s own top-left placement or any
 * future top-right element (coordinated live with the S07 session building
 * one).
 * S11 added a hunt-progress pill (`setHuntProgress`, hidden by default) in
 * the same `.br3d-hud` column as HP/super/coins — a review finding caught
 * that PLAN-CITY §5's "pick target -> tracked" line had no persistent
 * in-play readout, only the bodega's own full-screen HuntBoard overlay.
 */
export class Hud {
  private readonly hpFill: HTMLDivElement;
  private readonly hpLabel: HTMLDivElement;
  private readonly superFill: HTMLDivElement;
  private readonly superBtn: HTMLButtonElement;
  private readonly aimBtn: HTMLButtonElement;
  private readonly weaponBtn: HTMLButtonElement;
  private readonly coinLabel: HTMLDivElement;
  private readonly materialsChip: HTMLDivElement;
  private readonly huntPill: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  private readonly interactBtn: HTMLButtonElement;
  private readonly interactPrompt: HTMLDivElement;
  private readonly toast: HTMLDivElement;
  private toastHideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly bossHud: HTMLDivElement;
  private readonly bossLabel: HTMLDivElement;
  private readonly bossHpFill: HTMLDivElement;
  private superPressed = false;
  private aimCyclePressed = false;
  private weaponCyclePressed = false;
  private interactPressed = false;
  // I-1: previous-tick values, compared in update()/setMaterials() to decide
  // whether to pulse a chip — a chip only pulses when its OWN value just
  // increased, never on every tick regardless of change.
  private prevCoins = 0;
  private prevScrap = 0;
  private prevPart = 0;
  private static readonly MAX_FLY_ARCS = 6; // I-1: "fly-to-chip DOM arc (batched '×N', cap 6)"
  private activeFlyArcs = 0;

  constructor() {
    const root = document.createElement('div');
    root.className = 'br3d-hud';

    const hpBar = document.createElement('div');
    hpBar.className = 'br3d-hpbar';
    this.hpFill = document.createElement('div');
    this.hpFill.className = 'br3d-hpbar-fill';
    this.hpLabel = document.createElement('div');
    this.hpLabel.className = 'br3d-hpbar-label';
    hpBar.append(this.hpFill, this.hpLabel);

    const superBar = document.createElement('div');
    superBar.className = 'br3d-superbar';
    this.superFill = document.createElement('div');
    this.superFill.className = 'br3d-superbar-fill';
    superBar.append(this.superFill);

    this.coinLabel = document.createElement('div');
    this.coinLabel.className = 'br3d-coins';
    this.coinLabel.textContent = '0';

    // I-3 (PLAN-CITY3 §5): "HUD materials chip (⬡scrap ⚙part, hidden while
    // 0/0)" — parts had no display anywhere in the game before this; hidden
    // by default (setMaterials hides it again whenever both are 0, e.g. a
    // fresh save) so it doesn't clutter the HUD before the player has ever
    // picked up a material.
    this.materialsChip = document.createElement('div');
    this.materialsChip.className = 'br3d-materials-chip';
    this.materialsChip.style.display = 'none';

    // S11 review fix (HIGH, spec-fidelity): PLAN-CITY §5's own line says
    // "pick target -> tracked" — the hunt board could set a target, but
    // nothing showed live progress once the player walked back out to the
    // street to actually hunt it; the only place killedCount/targetCount
    // ever appeared was the full-screen board itself (bodega-only) or the
    // one-shot completion toast. A persistent HUD pill, hidden whenever no
    // hunt is active, closes that gap the same way every other live stat in
    // this class already does (HP/super/coins).
    this.huntPill = document.createElement('div');
    this.huntPill.className = 'br3d-hunt-pill';
    this.huntPill.style.display = 'none';

    root.append(hpBar, superBar, this.coinLabel, this.materialsChip, this.huntPill);
    document.body.append(root);

    this.superBtn = document.createElement('button');
    this.superBtn.className = 'br3d-super-btn';
    this.superBtn.textContent = 'SUPER';
    this.superBtn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.superPressed = true;
    });
    document.body.append(this.superBtn);

    this.aimBtn = document.createElement('button');
    this.aimBtn.className = 'br3d-aim-btn';
    this.aimBtn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.aimCyclePressed = true;
    });
    document.body.append(this.aimBtn);

    this.weaponBtn = document.createElement('button');
    this.weaponBtn.className = 'br3d-weapon-btn';
    this.weaponBtn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.weaponCyclePressed = true;
    });
    document.body.append(this.weaponBtn);

    this.banner = document.createElement('div');
    this.banner.className = 'br3d-banner';
    this.banner.style.display = 'none';
    document.body.append(this.banner);

    this.interactPrompt = document.createElement('div');
    this.interactPrompt.className = 'br3d-interact-prompt';
    this.interactPrompt.style.display = 'none';
    document.body.append(this.interactPrompt);

    this.interactBtn = document.createElement('button');
    this.interactBtn.className = 'br3d-interact-btn';
    this.interactBtn.textContent = 'USE';
    this.interactBtn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.interactPressed = true;
    });
    document.body.append(this.interactBtn);

    this.toast = document.createElement('div');
    this.toast.className = 'br3d-toast';
    this.toast.style.display = 'none';
    document.body.append(this.toast);

    // S06: top-center boss HP bar, hidden until BossArena activates an encounter.
    this.bossHud = document.createElement('div');
    this.bossHud.className = 'br3d-boss-hud';
    this.bossHud.style.display = 'none';
    this.bossLabel = document.createElement('div');
    this.bossLabel.className = 'br3d-boss-label';
    const bossBar = document.createElement('div');
    bossBar.className = 'br3d-boss-hpbar';
    this.bossHpFill = document.createElement('div');
    this.bossHpFill.className = 'br3d-boss-hpbar-fill';
    bossBar.append(this.bossHpFill);
    this.bossHud.append(this.bossLabel, bossBar);
    document.body.append(this.bossHud);
  }

  /** Edge-triggered, same convention as S00's JumpButton. */
  consumeSuperPress(): boolean {
    if (!this.superPressed) return false;
    this.superPressed = false;
    return true;
  }

  consumeAimCyclePress(): boolean {
    if (!this.aimCyclePressed) return false;
    this.aimCyclePressed = false;
    return true;
  }

  consumeWeaponCyclePress(): boolean {
    if (!this.weaponCyclePressed) return false;
    this.weaponCyclePressed = false;
    return true;
  }

  consumeInteractPress(): boolean {
    if (!this.interactPressed) return false;
    this.interactPressed = false;
    return true;
  }

  /** `label` is world/interact.ts's nearest in-range Interactable's own label text, or null when nothing's in range — called every tick from main.ts, cheap enough (one textContent/display write, skipped when nothing changed) not to need its own dirty-check. */
  setInteractPrompt(label: string | null): void {
    this.interactBtn.classList.toggle('active', label !== null);
    if (label === null) {
      this.interactPrompt.style.display = 'none';
      return;
    }
    this.interactPrompt.textContent = label;
    this.interactPrompt.style.display = 'block';
  }

  /** S11: `text` is a pre-formatted label (e.g. "🎯 Rat 2/5") — main.ts resolves the kind's display label via meta/hunts.ts's labelFor before calling this, keeping Hud itself decoupled from hunt-specific lookups (the same "take a pre-formatted string, not an id" convention `update()`'s `weapon.label` param already uses). `null` hides the pill entirely (no active hunt). Called every tick while playing, same cadence as `setInteractPrompt`. */
  setHuntProgress(text: string | null): void {
    if (text === null) {
      this.huntPill.style.display = 'none';
      return;
    }
    this.huntPill.textContent = text;
    this.huntPill.style.display = 'block';
  }

  /** I-1: forces a restart of `.pulse`'s CSS animation (style.css) even if it's already mid-run from a moment ago — removing then re-adding a class alone is a no-op without a reflow in between. */
  private pulse(el: HTMLElement): void {
    el.classList.remove('pulse');
    void el.offsetWidth; // force reflow
    el.classList.add('pulse');
  }

  /** I-3: `⬡scrap ⚙part`, hidden while both are 0 — called once per tick from main.ts alongside `update()`. Pulses whenever either value just increased (I-1's "chip pulse"). */
  setMaterials(scrap: number, part: number): void {
    if (scrap > this.prevScrap || part > this.prevPart) this.pulse(this.materialsChip);
    this.prevScrap = scrap;
    this.prevPart = part;
    if (scrap === 0 && part === 0) {
      this.materialsChip.style.display = 'none';
      return;
    }
    this.materialsChip.style.display = 'inline-flex';
    this.materialsChip.textContent = `⬡${scrap} ⚙${part}`;
  }

  private targetElFor(kind: LootKind): HTMLElement {
    if (kind === 'material' || kind === 'part') return this.materialsChip;
    if (kind === 'heart') return this.hpFill;
    return this.coinLabel; // coin, cosmetic, collectible
  }

  /**
   * I-1: "fly-to-chip DOM arc (batched '×N', cap 6)" — a small DOM label
   * eases from a world-projected screen point to the target chip's own
   * current position (`getBoundingClientRect()`, read fresh — never cached,
   * since the HUD's own layout can shift with `--br3d-ctl-scale`/lefty
   * mode). CSS `transition` drives the motion (see `.br3d-flyarc` in
   * style.css); the element removes itself once it's had time to finish.
   * Capped at `MAX_FLY_ARCS` concurrent arcs system-wide — beyond that, a
   * call is silently skipped (an under-animated frame during a big multi-
   * kill moment is a cosmetic-only compromise, never a functional loss,
   * same discipline `PickupPool.spawnBurst`'s own pool-reuse comment uses).
   */
  spawnFlyArc(fromX: number, fromY: number, kind: LootKind, count: number): void {
    if (this.activeFlyArcs >= Hud.MAX_FLY_ARCS) return;
    const target = this.targetElFor(kind);
    const rect = target.getBoundingClientRect();
    const toX = rect.left + rect.width / 2;
    const toY = rect.top + rect.height / 2;
    const el = document.createElement('div');
    el.className = 'br3d-flyarc';
    el.textContent = count > 1 ? `${FLY_ICON[kind]}×${count}` : FLY_ICON[kind];
    el.style.left = `${fromX}px`;
    el.style.top = `${fromY}px`;
    document.body.append(el);
    this.activeFlyArcs++;
    requestAnimationFrame(() => {
      el.style.transform = `translate(${toX - fromX}px, ${toY - fromY}px) scale(0.4)`;
      el.style.opacity = '0';
    });
    setTimeout(() => {
      el.remove();
      this.activeFlyArcs--;
    }, 500);
  }

  /**
   * Review fix (LOW, dont-break-list): a harness check for `spawnFlyArc`'s
   * cap can only clear the leftover `.br3d-flyarc` DOM nodes it can SEE —
   * `activeFlyArcs` itself (the actual gate `spawnFlyArc` checks) only ever
   * decrements inside each arc's own 500ms `setTimeout`, so a real gameplay
   * check earlier in the SAME page session (a real pickup collection) can
   * leave it nonzero for up to 500ms of real wall-clock time, flaking a
   * cap-boundary assertion that has nothing to do with the cap logic
   * itself. Test-only — same "expose the raw thing for direct testing"
   * convention as `economy/drops.ts`'s `__resetDropsRngForTest`.
   */
  debugResetFlyArcs(): void {
    this.activeFlyArcs = 0;
  }

  private static readonly TOAST_DURATION_MS = 2400;

  /**
   * A self-timed, self-clearing notification — checkpoint-discovery
   * ("CHECKPOINT — ROOFTOP"), the locked block-seam stub's message, and (Wave
   * L-4) a rare-drop announcement. Wall-clock timed (`setTimeout`), not
   * sim-`dt`-driven: purely cosmetic UI feedback, not gameplay/replay-
   * relevant state, so it doesn't need the deterministic-sim discipline §0.7
   * requires for anything a replay must reproduce. A second call while one
   * is already showing CLOBBERS it (deliberate, unchanged from before Wave
   * L — the harness's own subway-seam check depends on "the latest toast
   * always wins immediately," and a queued/delayed toast would silently
   * break that established contract for every existing caller, not just
   * cosmetic ones — see this class's own two-cosmetics-same-tick handling
   * further down, which solves that specific LOW-severity case WITHOUT
   * changing this method's general semantics).
   *
   * `rarity` is optional — every pre-L4 call site (a bare `showToast(text)`)
   * is unaffected and renders with the plain, non-tinted `.br3d-toast` style.
   */
  showToast(text: string, rarity?: Rarity): void {
    this.toast.textContent = text;
    this.toast.className = rarity ? `br3d-toast br3d-rarity-${rarity}` : 'br3d-toast';
    this.toast.style.display = 'block';
    if (this.toastHideTimer !== null) clearTimeout(this.toastHideTimer);
    this.toastHideTimer = setTimeout(() => {
      this.toast.style.display = 'none';
      this.toastHideTimer = null;
    }, Hud.TOAST_DURATION_MS);
  }

  update(hp: number, maxHp: number, superValue: number, superMax: number, preset: AimPreset, weapon: WeaponDef, coins: number): void {
    const hpPct = Math.max(0, hp / maxHp) * 100;
    this.hpFill.style.width = `${hpPct}%`;
    this.hpLabel.textContent = `${Math.ceil(hp)}/${maxHp}`;
    this.superFill.style.width = `${(superValue / superMax) * 100}%`;
    this.superBtn.classList.toggle('ready', superValue >= superMax);
    this.aimBtn.textContent = AIM_PRESET_LABELS[preset];
    this.weaponBtn.textContent = weapon.label;
    if (coins > this.prevCoins) this.pulse(this.coinLabel); // I-1: chip pulse
    this.prevCoins = coins;
    this.coinLabel.textContent = `${coins}`;
  }

  showBanner(text: string): void {
    this.banner.textContent = text;
    this.banner.style.display = 'block';
  }

  hideBanner(): void {
    this.banner.style.display = 'none';
  }

  showBossBar(): void {
    this.bossHud.style.display = 'block';
  }

  hideBossBar(): void {
    this.bossHud.style.display = 'none';
  }

  /** `label` is the boss's own display name (e.g. "GRIDLOCK GOLEM") — `exposed` toggles the gold EXPOSED cue, the HUD half of Classic's "gold ring/EXPOSED! overlay" (the visual half is `Boss.applyExposedTint` on the model itself). */
  updateBossBar(label: string, hp: number, maxHp: number, exposed: boolean): void {
    const pct = Math.max(0, hp / maxHp) * 100;
    this.bossHpFill.style.width = `${pct}%`;
    this.bossLabel.textContent = exposed ? `${label} — EXPOSED!` : label;
    this.bossHud.classList.toggle('exposed', exposed);
  }
}
